import React from "react";
import { Typography, IconButton } from "@mui/material";
import { getRiskLevelConfig } from "./mockMobilityData";
import { getParams } from "../../../../utlis/helpers";
import "./mobilityinsights.scss";
import StatusMetricBlock from "../../../common/StatusMetricBlock";
import NoDataFound from "../../../common/NoDataFound";
import { rewriteReportDomain } from "../../../../configs/environment";
import { useT } from "../../../../i18n/useT";
import { getCurrentLocale } from "../../../../utlis/locale";

/**
 * Mobility Insights Component
 *
 * Shows the assessment results passed via props
 */
const MobilityInsights = ({ assessmentData, onClose, onRetake }) => {
  const t = useT("riskInsights");
  const buildRomReportUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;

    const { accessToken, providerId, adminId, clientId } = getParams();
    const withV2 = String(rawUrl).replace(
      /side-tug-reportv1/g,
      "side-tug-reportv2",
    );
    // Force the configured REPORTS_URL domain regardless of what the backend returned.
    const domainRewritten = rewriteReportDomain(withV2);

    try {
      const url = new URL(domainRewritten);
      const paramsToAdd = {
        isWebsite: "true",
        accessToken,
        providerId,
        adminId,
        clientId,
      };
      Object.entries(paramsToAdd).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }
      });
      return url.toString();
    } catch {
      return domainRewritten;
    }
  };

  // Transform API data to display format
  const transformAssessmentData = (data) => {
    if (!data) return null;

    // Resolve risk level from multiple possible API field names
    const riskLevelRaw =
      data.riskLevel || data.risk_level || data.risk_of_fall || null;

    const normalizeRiskLevel = (val) => {
      if (!val) return null;
      const s = String(val).trim().toLowerCase();
      if (["high", "elevated", "low"].includes(s)) return s;
      if (s === "moderate" || s === "medium") return "elevated";
      if (s.includes("high")) return "high";
      if (s.includes("elevated")) return "elevated";
      if (s.includes("low")) return "low";
      return s;
    };

    const riskLevel = normalizeRiskLevel(riskLevelRaw);

    // Check if the model has finished processing
    const isProcessing = data.modelProcessed === false && !riskLevel;

    // Resolve assessment date
    const assessedAt =
      data.assessedAt || data.assessed_at || data.createdAt || null;

    // Resolve insights text
    const rawInsights = data.insights || data.summary || "";
    const insights = rawInsights.trim()
      ? rawInsights
      : isProcessing
        ? t("insights_processing")
        : t("insights_default");

    // Resolve recommendations - handle both array (recommendations) and string (recommendation)
    let recommendations;
    if (
      Array.isArray(data.recommendations) &&
      data.recommendations.length > 0
    ) {
      recommendations = data.recommendations;
    } else if (
      typeof data.recommendation === "string" &&
      data.recommendation.trim()
    ) {
      // API returns singular "recommendation" as a string - convert to array format
      recommendations = [{ text: data.recommendation, hasLink: false }];
    } else if (
      typeof data.recommendations === "string" &&
      data.recommendations.trim()
    ) {
      recommendations = [{ text: data.recommendations, hasLink: false }];
    } else {
      recommendations = isProcessing
        ? [
            {
              text: t("recs_processing"),
              hasLink: false,
            },
          ]
        : [
            {
              text: t("recs_default"),
              hasLink: false,
            },
          ];
    }

    // Resolve input summary from multiple possible API structures
    // API may return: input_summary { history, health, safety, meds }
    // or: inputSummary { fallHistory, healthConditions, safetyMeasures, medications }
    const apiSummary = data.inputSummary || data.input_summary || {};

    const inputSummary = {
      history: {
        label: t("summary_history"),
        value:
          apiSummary.history?.value ||
          apiSummary.history ||
          apiSummary.fallHistory?.value ||
          apiSummary.fallHistory ||
          data.fallHistory ||
          t("not_specified"),
        icon: "history",
      },
      health: {
        label: t("summary_health"),
        value:
          apiSummary.health?.value ||
          apiSummary.health ||
          apiSummary.healthConditions?.value ||
          apiSummary.healthConditions ||
          data.healthConditions ||
          t("not_specified"),
        icon: "health",
      },
      safety: {
        label: t("summary_safety"),
        value:
          apiSummary.safety?.value ||
          apiSummary.safety ||
          apiSummary.safetyMeasures?.value ||
          apiSummary.safetyMeasures ||
          data.safetyMeasures ||
          t("not_specified"),
        icon: "safety",
      },
      meds: {
        label: t("summary_meds"),
        value:
          apiSummary.meds?.value ||
          apiSummary.meds ||
          apiSummary.medications?.value ||
          apiSummary.medications ||
          data.medications ||
          t("not_specified"),
        icon: "meds",
      },
    };

    return {
      hasResults: true,
      isProcessing,
      riskLevel: riskLevel || "low",
      riskLevelRaw: riskLevel, // normalized; null if not yet computed
      assessedAt,
      insights,
      recommendations,
      inputSummary,
    };
  };

  const mobilityData = transformAssessmentData(assessmentData);

  const handleRetake = () => {
    onRetake?.();
  };

  const handleLinkAction = (action) => {
    switch (action) {
      case "measure_mobility":
        // TODO: Navigate to mobility measurement
        console.log("Navigate to measure mobility");
        break;
      case "wellness_report":
        if (assessmentData?.wellnessReportUrl) {
          window.location.href = buildRomReportUrl(
            assessmentData.wellnessReportUrl,
          );
        }
        break;
      default:
        console.log("Unknown action:", action);
    }
  };

  // Show message if no data
  if (!mobilityData || !mobilityData.hasResults) {
    return (
      <div className="mobility-insights-container no-results">
        <NoDataFound
          icon="mobility"
          message={t("no_assessment_data")}
          description={t("no_assessment_data_desc")}
          actionLabel={t("close")}
          onAction={onClose}
        />
      </div>
    );
  }

  // Icons for input summary
  const HistoryIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 3C8.03 3 4 7.03 4 12H1L4.89 15.89L4.96 16.03L9 12H6C6 8.13 9.13 5 13 5C16.87 5 20 8.13 20 12C20 15.87 16.87 19 13 19C11.07 19 9.32 18.21 8.06 16.94L6.64 18.36C8.27 19.99 10.51 21 13 21C17.97 21 22 16.97 22 12C22 7.03 17.97 3 13 3ZM12 8V13L16.28 15.54L17 14.33L13.5 12.25V8H12Z"
        fill="currentColor"
      />
    </svg>
  );

  const HealthIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z"
        fill="currentColor"
      />
    </svg>
  );

  const SafetyIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z"
        fill="currentColor"
      />
    </svg>
  );

  const MedsIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.22 11.29L11.29 4.22C13.64 1.88 17.44 1.88 19.78 4.22C22.12 6.56 22.12 10.36 19.78 12.71L12.71 19.78C10.36 22.12 6.56 22.12 4.22 19.78C1.88 17.44 1.88 13.64 4.22 11.29ZM5.64 12.71C4.07 14.27 4.07 16.82 5.64 18.36C7.2 19.93 9.76 19.93 11.29 18.36L14.83 14.83L9.17 9.17L5.64 12.71Z"
        fill="currentColor"
      />
    </svg>
  );

  const getIcon = (iconType) => {
    switch (iconType) {
      case "history":
        return <HistoryIcon />;
      case "health":
        return <HealthIcon />;
      case "safety":
        return <SafetyIcon />;
      case "meds":
        return <MedsIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const riskConfig = getRiskLevelConfig(
    mobilityData.riskLevelRaw,
    mobilityData.isProcessing,
  );
  const { insights, recommendations, inputSummary, assessedAt, isProcessing } =
    mobilityData;

  // Inline icons
  const CloseIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className="mobility-insights-container">
      {/* Header */}
      <div className="insights-header">
        <IconButton className="close-btn" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Assessment Date */}
      {assessedAt && (
        <div className="assessment-date">
          {t("assessed_on", {
            date: new Date(assessedAt).toLocaleDateString(getCurrentLocale()),
          })}
        </div>
      )}

      {/* Risk Level Banner */}
      <div className="risk-banner-container" style={{ margin: "0 0 12px 0" }}>
        <StatusMetricBlock
          label={t("risk_of_fall")}
          value={t(riskConfig.labelKey)}
          status={riskConfig.status}
        />
      </div>

      {/* Processing Notice */}
      {isProcessing && (
        <section className="content-section">
          <Typography className="section-text" style={{ fontStyle: "italic" }}>
            {t("processing_notice")}
          </Typography>
        </section>
      )}

      {/* Insights Section */}
      <section className="content-section">
        <Typography className="section-title">{t("insights")}</Typography>
        <Typography className="section-text">{insights}</Typography>
      </section>

      {/* Recommendation Section */}
      <section className="content-section">
        <Typography className="section-title">{t("recommendation")}</Typography>
        {recommendations.map((rec, index) => (
          <div key={index} className="recommendation-item">
            <Typography className="section-text">
              {rec.text}
              {rec.hasLink && (
                <button
                  className="inline-link"
                  onClick={() => handleLinkAction(rec.linkAction)}
                >
                  {rec.linkText}
                </button>
              )}
            </Typography>
          </div>
        ))}
      </section>

      {/* Ingested Input Summary */}
      <section className="content-section">
        <Typography className="section-title">{t("input_summary")}</Typography>
        <div className="input-summary-grid">
          {Object.entries(inputSummary).map(([key, item]) => (
            <div key={key} className="summary-item">
              <span className="summary-icon">{getIcon(item.icon)}</span>
              <div className="summary-content">
                <Typography className="summary-label">{item.label}</Typography>
                <Typography className="summary-value">{item.value}</Typography>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Retake Button */}
      <button
        className="btn btn-primary retake-assessment-btn"
        onClick={handleRetake}
      >
        {t("retake_assessment")}
      </button>
    </div>
  );
};

export default MobilityInsights;
